import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiX, FiShare2, FiMail, FiCalendar, FiDollarSign, FiTrash2, FiEye } = FiIcons;

function VehicleWishlist() {
  const [isOpen, setIsOpen] = useState(false);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedForShare, setSelectedForShare] = useState(null);

  // Load wishlist from localStorage on component mount
  useEffect(() => {
    const saved = localStorage.getItem('halifaxWishlist');
    if (saved) {
      setWishlistItems(JSON.parse(saved));
    }
  }, []);

  // Save to localStorage whenever wishlist changes
  useEffect(() => {
    localStorage.setItem('halifaxWishlist', JSON.stringify(wishlistItems));
  }, [wishlistItems]);

  const removeFromWishlist = (vehicleId) => {
    setWishlistItems(prev => prev.filter(item => item.id !== vehicleId));
  };

  const shareWishlist = (vehicle) => {
    setSelectedForShare(vehicle);
    setShareModalOpen(true);
  };

  const sendWishlistEmail = () => {
    const subject = "My Halifax Vehicle Wishlist";
    const body = `Check out the vehicles I'm interested in from Halifax Demo Motors:\n\n${
      wishlistItems.map(item => 
        `${item.year} ${item.make} ${item.model} - $${item.price.toLocaleString()} CAD`
      ).join('\n')
    }\n\nView full details at our Halifax showroom!`;
    
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const calculateTotalValue = () => {
    return wishlistItems.reduce((total, item) => total + item.price, 0);
  };

  // Mock data - in real app this would come from your liked vehicles
  useEffect(() => {
    if (wishlistItems.length === 0) {
      setWishlistItems([
        {
          id: 1,
          make: 'Audi',
          model: 'Q5 Quattro',
          year: 2023,
          price: 54999,
          image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=300&h=200&fit=crop',
          addedDate: new Date('2024-01-15'),
          priceChange: -1000,
          available: true
        },
        {
          id: 2,
          make: 'BMW',
          model: 'X3 xDrive',
          year: 2022,
          price: 48999,
          image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=300&h=200&fit=crop',
          addedDate: new Date('2024-01-10'),
          priceChange: 500,
          available: true
        }
      ]);
    }
  }, []);

  return (
    <>
      {/* Wishlist Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="fixed top-24 right-6 bg-red-500 text-white p-3 rounded-full shadow-lg z-40 hover:bg-red-600 transition-colors"
      >
        <SafeIcon icon={FiHeart} className="w-5 h-5" />
        {wishlistItems.length > 0 && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-xs font-bold">{wishlistItems.length}</span>
          </div>
        )}
      </motion.button>

      {/* Wishlist Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-pink-600 p-6 text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">My Halifax Wishlist</h2>
                    <p className="text-red-100">
                      {wishlistItems.length} vehicle{wishlistItems.length !== 1 ? 's' : ''} saved • 
                      Total value: ${calculateTotalValue().toLocaleString()} CAD
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={sendWishlistEmail}
                      className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors flex items-center space-x-2"
                    >
                      <SafeIcon icon={FiMail} className="w-4 h-4" />
                      <span>Share List</span>
                    </button>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 max-h-96 overflow-y-auto">
                {wishlistItems.length === 0 ? (
                  <div className="text-center py-12">
                    <SafeIcon icon={FiHeart} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Your wishlist is empty</h3>
                    <p className="text-gray-600 mb-6">
                      Start adding vehicles you love to keep track of them and get price alerts.
                    </p>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Browse Halifax Inventory
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {wishlistItems.map((vehicle, index) => (
                      <motion.div
                        key={vehicle.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-gray-50 rounded-lg p-4 flex items-center space-x-4"
                      >
                        <img
                          src={vehicle.image}
                          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                          className="w-24 h-16 object-cover rounded-lg"
                        />
                        
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-gray-900">
                              {vehicle.year} {vehicle.make} {vehicle.model}
                            </h3>
                            <div className="flex items-center space-x-2">
                              {vehicle.priceChange !== 0 && (
                                <span className={`text-sm font-medium ${
                                  vehicle.priceChange > 0 ? 'text-red-600' : 'text-green-600'
                                }`}>
                                  {vehicle.priceChange > 0 ? '+' : ''}${Math.abs(vehicle.priceChange).toLocaleString()}
                                </span>
                              )}
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                vehicle.available 
                                  ? 'bg-green-100 text-green-600' 
                                  : 'bg-red-100 text-red-600'
                              }`}>
                                {vehicle.available ? 'Available' : 'Sold'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xl font-bold text-blue-600">
                                ${vehicle.price.toLocaleString()} CAD
                              </p>
                              <p className="text-xs text-gray-500">
                                Added {vehicle.addedDate.toLocaleDateString()}
                              </p>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => shareWishlist(vehicle)}
                                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                              >
                                <SafeIcon icon={FiShare2} className="w-4 h-4" />
                              </button>
                              <button
                                className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-full transition-colors"
                              >
                                <SafeIcon icon={FiEye} className="w-4 h-4" />
                              </button>
                              <button
                                className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-colors"
                              >
                                <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => removeFromWishlist(vehicle.id)}
                                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                              >
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              {wishlistItems.length > 0 && (
                <div className="border-t border-gray-200 p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">
                        Get notified about price changes and new arrivals in Halifax
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2">
                        <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
                        <span>Get Financing</span>
                      </button>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                        <span>Schedule Viewing</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {shareModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4"
            onClick={() => setShareModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">Share Vehicle</h3>
              {selectedForShare && (
                <div className="mb-4">
                  <p className="text-gray-600">
                    {selectedForShare.year} {selectedForShare.make} {selectedForShare.model}
                  </p>
                  <p className="text-blue-600 font-bold">
                    ${selectedForShare.price.toLocaleString()} CAD
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <button className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center">
                  <SafeIcon icon={FiMail} className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">Email</span>
                </button>
                <button className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center">
                  <SafeIcon icon={FiShare2} className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">Copy Link</span>
                </button>
              </div>
              <button
                onClick={() => setShareModalOpen(false)}
                className="w-full mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default VehicleWishlist;